import { useState } from 'react';
import type { ReportNarrativeSection, ReportClassifier, Status } from '@/lib/types';

type Mode = 'narrative' | 'structured';

const ringColor: Record<Status, string> = {
  ok: 'stroke-[var(--c-ok)]',
  warn: 'stroke-[var(--c-warn)]',
  crit: 'stroke-[var(--c-crit)]',
};

const scoreText: Record<Status, string> = {
  ok: 'text-ok-deep',
  warn: 'text-warn-deep',
  crit: 'text-crit-deep',
};

interface NarrativeTabProps {
  narrative: ReportNarrativeSection[];
  classifiers: ReportClassifier[];
}

export function NarrativeTab({ narrative, classifiers }: NarrativeTabProps) {
  const [mode, setMode] = useState<Mode>('narrative');

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2.5 flex-wrap">
        <div className="glass-panel flex p-0.5 gap-0.5">
          {(
            [
              { value: 'narrative', label: 'Narrativa' },
              { value: 'structured', label: 'Estructurada' },
            ] as const
          ).map((o) => {
            const active = mode === o.value;
            return (
              <button
                key={o.value}
                type="button"
                onClick={() => setMode(o.value)}
                className={`px-3 py-1 rounded-[6px] text-[12px] font-medium transition-all cursor-pointer outline-none ${
                  active
                    ? 'bg-surface text-primary shadow-pill'
                    : 'bg-transparent text-tertiary hover:text-primary'
                }`}
              >
                {o.label}
              </button>
            );
          })}
        </div>
      </div>

      {mode === 'narrative' ? (
        <article className="glass-panel px-8 py-7 flex flex-col gap-7 max-[760px]:p-5 max-[760px]:gap-5">
          {narrative.map((section) => (
            <section key={section.title} className="flex flex-col gap-2">
              <h3 className="text-[15px] font-semibold tracking-[-0.01em] m-0">
                {section.title}
              </h3>
              {section.paragraphs.map((p, i) => (
                <p
                  key={i}
                  className="text-[13.5px] text-secondary leading-[1.65] m-0"
                >
                  {p}
                </p>
              ))}
            </section>
          ))}
        </article>
      ) : (
        <div className="grid grid-cols-2 gap-2.5 max-[760px]:grid-cols-1">
          {classifiers.map((c) => (
            <article
              key={c.name}
              className="glass-panel p-5 px-[22px] flex flex-col gap-3"
            >
              <header className="flex items-center justify-between gap-3">
                <h3 className="text-[14px] font-semibold tracking-[-0.005em] m-0">
                  {c.name}
                </h3>
                <ScoreRing score={c.score} status={c.status} />
              </header>
              <p className="text-[12.5px] text-tertiary leading-[1.55] m-0">
                {c.description}
              </p>
              <dl className="grid grid-cols-2 gap-y-1.5 gap-x-4 m-0 mt-1 pt-3 border-t border-line-glass">
                <Field label="Score" value={`${c.score}/100`} mono />
                <Field
                  label="Marcadores"
                  value={`${c.relatedCodes.length} biomarcador${c.relatedCodes.length === 1 ? '' : 'es'}`}
                />
              </dl>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

interface ScoreRingProps {
  score: number;
  status: Status;
}

function ScoreRing({ score, status }: ScoreRingProps) {
  const r = 18;
  const c = 2 * Math.PI * r;
  const dash = (score / 100) * c;
  return (
    <div className="relative w-[46px] h-[46px] shrink-0">
      <svg viewBox="0 0 44 44" className="w-full h-full -rotate-90">
        <circle
          cx="22"
          cy="22"
          r={r}
          fill="none"
          stroke="var(--border-default)"
          strokeWidth="3"
        />
        <circle
          cx="22"
          cy="22"
          r={r}
          fill="none"
          strokeWidth="3"
          strokeLinecap="round"
          className={ringColor[status]}
          strokeDasharray={`${dash} ${c - dash}`}
        />
      </svg>
      <div
        className={`absolute inset-0 grid place-items-center text-[14px] font-bold tnum tracking-[-0.02em] ${scoreText[status]}`}
      >
        {score}
      </div>
    </div>
  );
}

interface FieldProps {
  label: string;
  value: string;
  mono?: boolean;
}

function Field({ label, value, mono }: FieldProps) {
  return (
    <div>
      <dt className="text-[10px] font-semibold tracking-[0.06em] uppercase text-muted m-0">
        {label}
      </dt>
      <dd className={`text-[13px] font-medium text-secondary m-0 mt-0.5 ${mono ? 'tnum' : ''}`}>
        {value}
      </dd>
    </div>
  );
}
